import { html, PropertyValues } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import { ChatContextItem, Api, ChatSession, StoredChatSession } from "../api.js";
import { getSourcePath, MediaItem } from "../data/types.js";
import { ChatState } from "../pages/chat-state.js";
import { aiIcon, closeIcon, sendIcon } from "../utils/icons.js";
import { BaseElement } from "../utils/ui-components.js";
import { PreviewContext } from "./preview";
import { AudioState } from "./audio-player.js";
import { router } from "../utils/routing.js";

const chatState = ChatState.getInstance();

@customElement("chat-view")
export class ChatView extends BaseElement {
    @property({ type: Boolean })
    showHeader = true;

    private unsubscribe?: () => void;

    @state()
    private inputMessage = "";

    @state()
    private error: string | null = null;

    @state()
    private autoScroll = true;

    @query(".messages-container")
    private messagesContainer!: HTMLElement;

    @property()
    onAudioRequest?: (state: AudioState) => void;

    @state()
    private editingMessageIndex: number | null = null;

    @state()
    private editingContent = "";

    @state()
    private contextCollapsed = false;

    @property()
    onClose?: () => void;

    @property()
    onShowPreview?: (context: PreviewContext) => void;

    @state()
    private showHistory = false;

    private async handleSubmit(e: Event) {
        e.preventDefault();
        this.autoScroll = true;
        this.error = null;
        if (!chatState.session || !chatState.session.context?.length) return;
        if (!this.inputMessage.trim()) return;

        const userMessage = this.inputMessage.trim();
        this.inputMessage = "";

        const result = await chatState.complete(userMessage, () => {
            this.requestUpdate();
            if (this.autoScroll) {
                this.scrollToBottom();
            }
        });

        if (!result) {
            this.error = "Failed to send message";
        }
    }

    private scrollToBottom() {
        const container = this.messagesContainer;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }

    private renderMessage(content: string, isAssistant: boolean) {
        if (!isAssistant) return content;

        // Show loading indicator for empty assistant messages
        if (!content) {
            return html`
                <div class="flex gap-1 items-center min-h-[24px] py-1">
                    <span class="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span class="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span class="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></span>
                </div>
            `;
        }

        let parsedContent = marked(content, { async: false });

        const template = document.createElement("template");
        template.innerHTML = parsedContent;
        const fragment = template.content;

        // Replace references of the form [0.1.2] or [0.19.4-6] or [0.0.273-0.0.312]
        fragment.querySelectorAll("*").forEach((element) => {
            const text = element.innerHTML;
            element.innerHTML = text.replace(
                /\[(\d+(?:\.\d+)*(?:-\d+|-\d+\.\d+\.\d+)?(?:\s*,\s*\d+(?:\.\d+)*(?:-\d+|-\d+\.\d+\.\d+)?)*)\]/g,
                (match, refs) => {
                    const references = refs.split(",").map((ref: string) => {
                        // For each reference, only take the part before any range indicator
                        return ref.trim().split("-")[0];
                    });

                    return references
                        .map((ref: string) => {
                            const indices = ref.split(".").map(Number);
                            const contextIndex = indices[0];

                            if (!chatState.session?.context) {
                                return match;
                            }

                            if (contextIndex >= chatState.session.context.length) {
                                return match;
                            }

                            // Rest of the rendering code remains the same
                            const ctx = chatState.session.context[contextIndex];
                            const sourcePath = getSourcePath(ctx.item.path);
                            const date = new Date(ctx.item.date);
                            const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
                                date.getDate()
                            ).padStart(2, "0")}`;

                            return `
                        <span class="inline-flex items-center gap-2 bg-gray-100 text-xs text-gray-700 px-2 py-1 rounded-md">
                            <img src="/img/${sourcePath}.jpg" alt="" class="w-4 h-4 rounded object-cover" />
                            <button
                                class="hover:text-gray-900"
                                data-ref="${ref}"
                            >
                                ${ctx.item.title}
                                <span class="text-gray-400 text-xs ml-1">${formattedDate}</span>
                            </button>
                        </span>
                    `;
                        })
                        .join(" ");
                }
            );
        });

        // Instead of adding listeners to each button, add one delegate listener to the container
        setTimeout(() => {
            const container = this.renderRoot.querySelector(".messages-container");
            if (!container) return;
            container.removeEventListener("click", this.handleReferenceClick);
            container.addEventListener("click", this.handleReferenceClick);
        }, 0);

        const div = document.createElement("div");
        div.appendChild(fragment);
        return unsafeHTML(div.innerHTML);
    }

    // Define the click handler as a class method
    private handleReferenceClick = (e: Event) => {
        const button = (e.target as Element).closest("button[data-ref]");
        if (!button) return;
        if (!chatState.session) return;

        const ref = button.getAttribute("data-ref");
        const indices = (ref || "").split(".").map(Number);
        const contextIndex = indices[0];

        const ctx = chatState.session.context[contextIndex];
        if (!ctx) return;

        let matches = [];

        if (ctx.type == "segment") {
            const segmentIndex = ctx.item.segments?.findIndex((s) => s == ctx.segment) ?? -1;
            const subtitleIndex = indices[1];

            if (subtitleIndex !== undefined) {
                matches.push({
                    type: "subtitle",
                    segmentIndex,
                    subtitleIndex,
                    highlights: {
                        text: ctx.segment.subtitles?.[subtitleIndex]?.text || "",
                    },
                });
            } else {
                matches.push({
                    type: "segment",
                    segmentIndex,
                    highlights: {
                        text: ctx.segment.text,
                        title: ctx.segment.title,
                    },
                });
            }
        } else {
            const [, segmentIndex, subtitleIndex] = indices;
            if (segmentIndex !== undefined) {
                const segment = ctx.item.segments?.[segmentIndex];
                if (segment) {
                    if (subtitleIndex !== undefined) {
                        matches.push({
                            type: "subtitle",
                            segmentIndex,
                            subtitleIndex,
                            highlights: {
                                text: segment.subtitles?.[subtitleIndex]?.text || "",
                            },
                        });
                    } else {
                        matches.push({
                            type: "segment",
                            segmentIndex,
                            highlights: {
                                text: segment.text,
                                title: segment.title,
                            },
                        });
                    }
                }
            }
        }

        this.showPreview(
            {
                context: ctx,
                matches,
            },
            e
        );
    };

    private handleScroll() {
        const container = this.messagesContainer;
        if (!container) return;

        // If we're not at the bottom, user has scrolled up
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
        this.autoScroll = isAtBottom;
    }

    updated(changedProperties: PropertyValues) {
        super.updated(changedProperties);

        // Scroll to bottom if autoScroll is enabled
        if (this.autoScroll) {
            this.scrollToBottom();
        }
    }

    private showPreview(previewContext: PreviewContext, e: Event) {
        e.preventDefault();
        this.onShowPreview?.(previewContext);
    }

    connectedCallback() {
        super.connectedCallback();
        this.unsubscribe = chatState.subscribe(() => this.requestUpdate());
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.unsubscribe?.();
    }

    private handleDeleteMessage(index: number) {
        chatState.deleteMessage(index);
    }

    private handleEditMessage(index: number, content: string) {
        this.editingMessageIndex = index;
        this.editingContent = content;
    }

    private async handleSaveEdit() {
        if (this.editingMessageIndex !== null) {
            // Clear editing state first
            const editedContent = this.editingContent;
            const messageIndex = this.editingMessageIndex;
            this.editingMessageIndex = null;
            this.editingContent = "";
            this.error = null;

            // Update the message and get new response
            chatState.editUserMessage(messageIndex, editedContent);

            const result = await chatState.complete(editedContent, () => {
                this.requestUpdate();
                if (this.autoScroll) {
                    this.scrollToBottom();
                }
            });

            if (!result) {
                this.error = "Failed to get response";
            }
        }
    }

    private handleCancelEdit() {
        this.editingMessageIndex = null;
        this.editingContent = "";
    }

    private async handleChatSelect(storedSession: StoredChatSession) {
        this.showHistory = false;

        // Get unique item IDs from context
        const itemIds = [...new Set(storedSession.context.map((ctx) => ctx.itemId))];

        // Fetch all items referenced in the context
        const items = new Map<string, MediaItem>();
        const fetchedItems = await Api.getItemsByIds(itemIds);
        for (const item of fetchedItems) {
            items.set(item.id, item);
        }

        // Reconstruct the chat context
        const context = storedSession.context
            .map((ctx) => {
                const item = items.get(ctx.itemId);
                if (!item) return null;

                if (ctx.type === "segment" && typeof ctx.segmentIndex === "number" && item.segments) {
                    return {
                        type: "segment" as const,
                        item,
                        segment: item.segments[ctx.segmentIndex],
                    };
                }

                return {
                    type: "item" as const,
                    item,
                };
            })
            .filter((ctx): ctx is ChatContextItem => ctx !== null);

        // Create the chat session
        const session: ChatSession = {
            id: storedSession.id,
            messages: storedSession.messages,
            context,
            lastModified: new Date(storedSession.last_modified).getTime(),
        };

        // Update the chat state
        chatState.setSession(session);
    }

    private async handleShowHistory() {
        this.showHistory = true;
        router.pushModal(() => (this.showHistory = false));
    }

    render() {
        return html`
            ${this.showHistory
                ? html`<chat-history
                      .onSelect=${(session: StoredChatSession) => {
                          this.showHistory = false;
                          this.handleChatSelect(session);
                      }}
                      .onClose=${() => {
                          this.showHistory = false;
                          router.pop();
                      }}
                  ></chat-history>`
                : null}
            <div class="h-full flex flex-col overflow-hidden relative">
                <!-- Desktop layout with margins and rounded corners -->
                <div class="h-full flex flex-col lg:m-4 lg:bg-white lg:rounded-xl lg:shadow-sm overflow-hidden">
                    <!-- Header - only show on desktop or when showHeader is true -->
                    ${this.showHeader
                        ? html`
                              <div class="flex items-center justify-between h-12 px-6 border-b">
                                  <div class="flex items-center gap-2">
                                      <i class="w-5 h-5 icon">${aiIcon}</i>
                                      <h2 class="text-base font-medium text-gray-900">AI Assistant</h2>
                                  </div>
                                  <div class="flex items-center gap-2">
                                      <button
                                          @click=${() => this.handleShowHistory()}
                                          class="text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                                      >
                                          History
                                      </button>
                                      <button
                                          @click=${() => chatState.newSession()}
                                          class="text-sm text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                                      >
                                          New Session
                                      </button>
                                      <button @click=${() => this.onClose?.()} class="lg:hidden p-2 hover:text-gray-600">
                                          <i class="w-4 h-4 icon">${closeIcon}</i>
                                      </button>
                                  </div>
                              </div>
                          `
                        : null}

                    <!-- Error Message -->
                    ${this.error
                        ? html`
                              <div class="flex-shrink-0 px-4 py-2 bg-red-50">
                                  <p class="text-xs text-red-600">${this.error}</p>
                              </div>
                          `
                        : null}

                    <!-- Context Items -->
                    ${chatState.session?.context.length
                        ? html`
                              <div class="flex flex-col border-b border-gray-200 bg-gray-50">
                                  <button
                                      @click=${() => (this.contextCollapsed = !this.contextCollapsed)}
                                      class="flex items-center justify-between px-4 py-2 text-xs text-gray-500 hover:text-gray-700"
                                  >
                                      <span>
                                          Answering questions based on ${chatState.session.context.length}
                                          item${chatState.session.context.length === 1 ? "" : "s"}
                                          (${Math.round(chatState.calculateTotalTokens() / 1000)}k tokens)
                                      </span>
                                      <svg
                                          class="w-4 h-4 transform transition-transform ${this.contextCollapsed ? "-rotate-90" : "rotate-0"}"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                      >
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                      </svg>
                                  </button>
                                  <div
                                      class="overflow-hidden transition-all duration-200 ease-in-out"
                                      style="max-height: ${this.contextCollapsed ? "0" : "8rem"}"
                                  >
                                      <div class="px-4 pb-3 overflow-y-auto max-h-32">
                                          <div class="flex flex-wrap gap-2">
                                              ${chatState.session.context.map(
                                                  (ctx, index) => html`
                                                      <div class="flex items-center gap-2 bg-gray-100 text-xs text-gray-700 px-2 py-1 rounded-md">
                                                          <img
                                                              src="/img/${getSourcePath(ctx.item.path)}.jpg"
                                                              alt=""
                                                              class="w-4 h-4 rounded object-cover"
                                                          />
                                                          <button
                                                              @click=${(e: Event) =>
                                                                  this.showPreview(
                                                                      {
                                                                          context: ctx,
                                                                          matches: [],
                                                                      },
                                                                      e
                                                                  )}
                                                              class="truncate text-left hover:text-gray-900"
                                                          >
                                                              ${ctx.item.title} ${ctx.type == "segment" ? html` › ${ctx.segment.title}` : ""}
                                                              <span class="text-gray-400 text-xs ml-1"
                                                                  >${new Date(ctx.item.date).getFullYear()}-${String(
                                                                      new Date(ctx.item.date).getMonth() + 1
                                                                  ).padStart(2, "0")}-${String(new Date(ctx.item.date).getDate()).padStart(
                                                                      2,
                                                                      "0"
                                                                  )}</span
                                                              >
                                                          </button>
                                                          <button
                                                              @click=${() => chatState.removeContextItem(index)}
                                                              class="text-gray-400 hover:text-gray-600 p-0.5 -mr-0.5"
                                                          >
                                                              <i class="w-3 h-3 icon">${closeIcon}</i>
                                                          </button>
                                                      </div>
                                                  `
                                              )}
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          `
                        : null}

                    <!-- Messages -->
                    <div class="messages-container flex-1 overflow-y-auto p-4 space-y-4" @scroll=${this.handleScroll}>
                        ${chatState.session?.context.length
                            ? html`
                                  ${chatState.session.messages
                                      .filter((m) => m.role != "system")
                                      .map(
                                          (message, index) => html`
                                              <div class="chat-message ${message.role == "user" ? "user" : "assistant"} group">
                                                  <div class="flex justify-between items-start gap-2">
                                                      <div class="flex-1">
                                                          ${this.editingMessageIndex === index && message.role === "user"
                                                              ? html`
                                                                    <div class="flex flex-col gap-2">
                                                                        <textarea
                                                                            class="w-full p-2 border rounded-md"
                                                                            .value=${this.editingContent}
                                                                            @input=${(e: Event) =>
                                                                                (this.editingContent = (e.target as HTMLTextAreaElement).value)}
                                                                        ></textarea>
                                                                        <div class="flex gap-2">
                                                                            <button
                                                                                @click=${() => this.handleSaveEdit()}
                                                                                class="px-2 py-1 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                                                                            >
                                                                                Resubmit
                                                                            </button>
                                                                            <button
                                                                                @click=${() => this.handleCancelEdit()}
                                                                                class="px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                `
                                                              : html`
                                                                    <div class="prose max-w-none [&>:first-child]:mt-0 [&>:last-child]:mb-0">
                                                                        ${this.renderMessage(message.content, message.role == "assistant")}
                                                                    </div>
                                                                `}
                                                      </div>
                                                      <div class="lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                                                          ${message.role === "user" && this.editingMessageIndex !== index
                                                              ? html`
                                                                    <button
                                                                        @click=${() => this.handleEditMessage(index, message.content)}
                                                                        class="p-1 text-gray-400 hover:text-gray-600"
                                                                        title="Edit message"
                                                                    >
                                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path
                                                                                stroke-linecap="round"
                                                                                stroke-linejoin="round"
                                                                                stroke-width="2"
                                                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                `
                                                              : null}
                                                          <button
                                                              @click=${() => this.handleDeleteMessage(index)}
                                                              class="p-1 text-gray-400 hover:text-gray-600"
                                                              title="Delete message"
                                                          >
                                                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                  <path
                                                                      stroke-linecap="round"
                                                                      stroke-linejoin="round"
                                                                      stroke-width="2"
                                                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                  />
                                                              </svg>
                                                          </button>
                                                      </div>
                                                  </div>
                                              </div>
                                          `
                                      )}
                              `
                            : html`
                                  <div class="flex-1 flex items-center justify-center p-4">
                                      <div class="text-center max-w-sm">
                                          <div class="mb-4 flex justify-center">
                                              <i class="w-12 h-12 icon text-gray-400">${aiIcon}</i>
                                          </div>
                                          <h3 class="text-xl font-medium text-gray-900 mb-3">Add content to start chatting</h3>
                                          <p class="text-sm text-gray-500">
                                              Click the <i class="w-4 h-4 icon !inline-flex align-middle -mt-0.5">${aiIcon}</i> button on search
                                              results to add them as context for your questions.
                                          </p>
                                      </div>
                                  </div>
                              `}
                    </div>

                    <!-- Input Area with Token Usage -->
                    ${chatState.session?.context.length
                        ? html`
                              <div class="flex-shrink-0 bg-white border-t border-gray-200">
                                  <div class="p-4">
                                      <form class="flex items-start gap-2 rounded-xl bg-gray-50 p-3 relative" @submit=${this.handleSubmit}>
                                          <textarea
                                              class="flex-1 text-sm text-gray-700 placeholder-gray-400 resize-none outline-none bg-transparent min-h-[24px] max-h-32"
                                              placeholder="Ask questions about your search results..."
                                              .value=${this.inputMessage}
                                              @input=${(e: Event) => {
                                                  const textarea = e.target as HTMLTextAreaElement;
                                                  this.inputMessage = textarea.value;
                                                  textarea.style.height = "auto";
                                                  textarea.style.height = `${textarea.scrollHeight}px`;
                                              }}
                                              @keydown=${(e: KeyboardEvent) => {
                                                  if (e.key == "Enter" && !e.shiftKey) {
                                                      e.preventDefault();
                                                      this.handleSubmit(e);
                                                  }
                                              }}
                                          ></textarea>
                                          <div class="flex flex-col items-end gap-1">
                                              <button
                                                  type="submit"
                                                  class="text-blue-600 hover:text-blue-700 disabled:opacity-50 p-1"
                                                  ?disabled=${!this.inputMessage?.trim()}
                                              >
                                                  <i class="w-4 h-4 icon">${sendIcon}</i>
                                              </button>
                                              <!-- Token Usage -->
                                              <div class="flex gap-2 text-[10px] text-gray-400 leading-none">
                                                  <div class="flex items-center gap-0.5">
                                                      <svg class="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                          <path
                                                              stroke-linecap="round"
                                                              stroke-linejoin="round"
                                                              stroke-width="2"
                                                              d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                                                          />
                                                      </svg>
                                                      <span>${chatState.tokenUsage.inputTokens.toLocaleString()}</span>
                                                  </div>
                                                  <div class="flex items-center gap-0.5">
                                                      <svg class="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                          <path
                                                              stroke-linecap="round"
                                                              stroke-linejoin="round"
                                                              stroke-width="2"
                                                              d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"
                                                          />
                                                      </svg>
                                                      <span>${chatState.tokenUsage.outputTokens.toLocaleString()}</span>
                                                  </div>
                                              </div>
                                          </div>
                                      </form>
                                  </div>
                              </div>
                          `
                        : null}
                </div>
            </div>
        `;
    }
}
